import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { companyApi, ApiError, type CompanySummary } from '../lib/api';
import { useAuth } from './auth/AuthProvider';

interface CompanyContextValue {
  companies: CompanySummary[];
  active: CompanySummary | null;
  loading: boolean;
  /**
   * Gesetzt, wenn die Firmenliste NICHT geladen werden konnte (401/429/5xx).
   * Muss strikt von "erfolgreich geladen, aber leer" unterschieden werden:
   * sonst landet ein Bestandskunde bei einem Netzwerkfehler im Onboarding und
   * legt versehentlich eine zweite Firma an (siehe RequireCompany).
   */
  error: string | null;
  setActive: (id: string) => void;
  reload: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);

const STORAGE_KEY = 'saldify_active_company';

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Gesamt-Ladezustand: solange Auth ODER Firmen laden, ist es "loading".
  // Verhindert, dass RequireCompany bei F5 vorschnell zu /onboarding leitet,
  // bevor der Auth-Bootstrap (Refresh-Cookie) abgeschlossen ist.
  const loading = authLoading || companiesLoading;

  // Voruebergehende Fehler (Rate-Limit, Server/Netz) sind erneut versuchbar —
  // ein 401 dagegen nicht: dort muss der Auth-Layer den Refresh/Login machen.
  const isRetryable = (err: unknown): boolean =>
    !(err instanceof ApiError) || err.status === 429 || err.status >= 500;

  async function load() {
    setCompaniesLoading(true);
    setError(null);
    // Bis zu 3 Versuche mit Backoff (0.5s, 1.5s) — deckt das 429 des
    // API-Rate-Limits ("retry in 3 seconds") und kurze Netzaussetzer ab.
    const delays = [500, 1500];
    for (let attempt = 0; ; attempt++) {
      try {
        const list = await companyApi.list();
        setCompanies(list);
        setError(null);
        // Aktive Firma validieren / Default setzen.
        setActiveId((prev) => {
          if (prev && list.some((c) => c.id === prev)) return prev;
          return list[0]?.id ?? null;
        });
        break;
      } catch (err) {
        if (attempt < delays.length && isRetryable(err)) {
          await new Promise((r) => setTimeout(r, delays[attempt]));
          continue;
        }
        // WICHTIG: die bisherige Liste NICHT leeren. Eine leere Liste bedeutet
        // in RequireCompany "User hat keine Firma" und loest den Redirect ins
        // Onboarding aus — bei einem blossen Ladefehler waere das falsch.
        setError(err instanceof ApiError ? err.message : 'Firmen konnten nicht geladen werden.');
        break;
      }
    }
    setCompaniesLoading(false);
  }

  useEffect(() => {
    // Erst handeln, wenn der Auth-Bootstrap durch ist.
    if (authLoading) return;
    if (user) void load();
    else {
      setCompanies([]);
      setActiveId(null);
      setError(null);
      setCompaniesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  useEffect(() => {
    if (activeId) localStorage.setItem(STORAGE_KEY, activeId);
  }, [activeId]);

  const active = companies.find((c) => c.id === activeId) ?? null;

  return (
    <CompanyContext.Provider
      value={{ companies, active, loading, error, setActive: setActiveId, reload: load }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCompany(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany muss innerhalb von <CompanyProvider> verwendet werden.');
  return ctx;
}
